import qs from 'qs'
import { PropsWithChildren, useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PageId,
  PageIdExtra,
  PageIdInfo,
  PageIdSearchParams,
  usePageIdContext,
} from '@/providers/page-id-context'
import { useOpenServiceUrl } from '@/hooks'
import { Dictionary, SearchParams } from '@tmap-web-lib/utils'
import { RoutePath } from './paths'
import { useSelector } from 'react-redux'
import { selectSettingsIsCarLifeTermsAgreed, setIsCarLifeTermsAgreed } from '@/store/settings'
import { useGetTermsGroupWithAgreement } from '@/react-query'
import { store } from '@/store'

export const TERMS_GROUP_CARLIFE = 'GROUP_CARLIFE'

function CheckPageId(props: PropsWithChildren) {
  const { children } = props

  const isCarLifeTermsAgreed = useSelector(selectSettingsIsCarLifeTermsAgreed)
  const { data: termsGroupData, isInitialLoading } = useGetTermsGroupWithAgreement({ termsGroupCode: TERMS_GROUP_CARLIFE })
  const navigate = useNavigate()
  const pageIdContext = usePageIdContext()
  const openServiceUrl = useOpenServiceUrl()

  const [isReady, setReady] = useState(!pageIdContext.pageId)

  const replaceGeneralPage = useCallback((
    pageId: PageId,
    searchParams: SearchParams<PageIdSearchParams>,
    extraObject: PageIdExtra & Dictionary<string | number | boolean | undefined>,
    url: RoutePath | string,
    path: string,
  ) => {
    const logBoxType = extraObject.logbox?.type
    const isReplaceRoute = pageId === 'main'
      || (pageId === 'insur_vehicle_all' && logBoxType === 'menu_all')
      || (pageId === 'driving_history' && logBoxType === 'favorite')

    if (isReplaceRoute) {
      navigate({
        pathname: url,
        search: qs.stringify({ ...searchParams }),
      }, { replace: true })
      return
    }
    openServiceUrl(path)
  }, [navigate, openServiceUrl])

  const movePageTo = useCallback((pageIdInfo: PageIdInfo<PageId>, extraObject: PageIdExtra & Dictionary<string | number | boolean | undefined>, searchParams: SearchParams<PageIdSearchParams>, encodedExtraObject: string, isTscoreScheme: boolean) => {
    if (pageIdInfo && !isInitialLoading && isCarLifeTermsAgreed !== undefined) {
      const { url, pageId } = pageIdInfo
      const needTermsPage =
        pageId === 'driving_analysis'
        || pageId === 'vehicle'
        || pageId === 'vehicle_detail'
        || pageId === 'insurance_tab'
        || pageId === 'driving_history'
        || pageId === 'trip_history'
      const path = `${url}?extra=${encodedExtraObject}`

      if (isCarLifeTermsAgreed === false) {
        const extraObj = { ...extraObject, move_to: pageId }
        if (isTscoreScheme) {
          if (needTermsPage) {
            navigate({
              pathname: RoutePath.Etc_terms,
              search: qs.stringify({ ...extraObj }),
            }, { replace: true })
          } else {
            navigate({
              pathname: url,
              search: qs.stringify({ ...searchParams }),
            }, { replace: true })
          }
        } else {
          if (needTermsPage) {
            openServiceUrl(`tmap://life?pageid=etc_terms&extra=${encodeURIComponent(JSON.stringify(extraObj))}`)
          } else {
            replaceGeneralPage(pageId, searchParams, extraObject, url, path)
          }
        }
      } else if (isCarLifeTermsAgreed === true) {
        if (isTscoreScheme) {
          navigate({
            pathname: url,
            search: qs.stringify({ ...searchParams }),
          }, { replace: true })
        } else {
          replaceGeneralPage(pageId, searchParams, extraObject, url, path)
        }
      }
      setReady(true)
    }
  }, [isInitialLoading, isCarLifeTermsAgreed, navigate, openServiceUrl, replaceGeneralPage])

  useEffect(() => {
    if (!termsGroupData) return
    const termsList = termsGroupData.data?.displayGroupTermsSectionDtoList?.[0]?.displayGroupTermsCodeDtoList
    const terms =  termsList?.filter(term => term.isRequired).every(term => term.userAgreementDto?.isAgreed)
    store.dispatch(setIsCarLifeTermsAgreed(!!terms))
  }, [termsGroupData])

  useEffect(() => {
    const { extraObject, pageIdInfo, searchParams } = pageIdContext
    const encodedExtraObject = encodeURIComponent(JSON.stringify(extraObject))

    /**
     * 타 서비스에서 tscore를 사용한 상세 페이지를 열기를 원하면 반드시 targetScheme에 tscore 넣어서 호출하도록 가이드
     * 10.0부터 사용자가 보내는 값으로 적용되도록 수정 예정
     * tmap://tscore 스킴은 기본 랜딩 url이 '/web/insurance' 로 되어있기 때문에 다이렉트로 다른 페이지를 열어주려고 한다면 해당 url로 replace 해주어야 함.
     */
    const isTscoreScheme = extraObject?.targetScheme === 'tscore'
    movePageTo(pageIdInfo, extraObject, searchParams, encodedExtraObject, isTscoreScheme)
  }, [pageIdContext, movePageTo])

  return isReady ? <>{children}</> : null
}

export { CheckPageId }
